import { computed, inject, provide, reactive, ref, shallowRef } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { log } from '@/utils/core/vueLogger.js';
import { getCampaign, modelWithPatch } from '@/utils/domain/promo/promoModel';
import { validateStep } from '@/utils/domain/promo/promoValidation';
import { applyNegoChange, clearAllNegos, negotiateAll } from '@/utils/domain/promo/promoNego';
import { PRESENCE, applyStatementChange, hasStatedData, setAllPresence } from '@/utils/domain/promo/promoStatement';
import { addAdditional, removeAdditional } from '@/utils/domain/promo/promoAdditional';
import { addFree, applyFreeChange, removeFree } from '@/utils/domain/promo/promoFree';
import { addLocalProducts, applyLocalOfferComment, applyLocalOfferDate, createLocalOffers, localOfferCandidates, localProductCandidates, removeLocalProduct } from '@/utils/domain/promo/promoLocalOffer';
import { applyOfferComment, applyStatementEndDate } from '@/utils/domain/promo/promoOfferInfo';
import { autoSelectPromoPlan, forecastPlanKeys, setOfferOnPromoPlan } from '@/utils/domain/promo/promoPlan';
import { checkLineOrder } from '@/utils/domain/promo/promoOrder';
import { canDeactivateForecast, deactivateForecastPatch, forecastRecordPatch } from '@/utils/domain/promo/promoForecast';
import { toInputDate } from '@/utils/domain/promo/promoPrimitives';

// State of the Vue promo step (#23521) : the snapshot pushed by the legacy, the continuous validation, and the write
// channel. Every input goes through a domain rule, then `commit` hands the result to the legacy, which stores it, saves
// the draft and returns a fresh snapshot (no rule applied on the legacy side).

const KEY = Symbol('promoStep');

function cloneSnapshot(detail) {
	try {
		return structuredClone(detail);
	} catch {
		return JSON.parse(JSON.stringify(detail));
	}
}

export function providePromoStep() {
	const t = useTranslator();
	const snapshot = shallowRef(null);
	const notice = reactive({ visible: false, text: '', color: 'warning' });
	const confirmState = reactive({ visible: false, title: '', text: '', icon: 'help', confirmLabel: '', cancelLabel: '', resolve: null });
	const pickerState = reactive({ visible: false, kind: '', title: '', items: [], resolve: null });
	const compact = ref(false);
	let target = null;
	let planChecked = false;

	const validation = computed(() => (snapshot.value ? validateStep(snapshot.value) : null));

	const attach = (el) => {
		target = el;
	};

	function receive(detail) {
		if (!detail) return;
		if (snapshot.value && detail.revision === snapshot.value.revision && detail.instance === snapshot.value.instance) return;
		snapshot.value = cloneSnapshot(detail);
		if (!planChecked) {
			planChecked = true;
			selectPromoPlanOnLoad();
		}
	}

	/** `checkForOfferSelection` ran when each campaign was rendered : the offers already negotiated go on the promo plan. */
	function selectPromoPlanOnLoad() {
		let model = snapshot.value.model;
		let promoPlanOffers = null;
		for (const campaign of snapshot.value.campaigns || []) {
			const plan = autoSelectPromoPlan(snapshot.value, campaign.id, model);
			if (!plan) continue;
			promoPlanOffers = { ...promoPlanOffers, ...plan.promoPlanOffers };
			model = modelWithPatch(model, plan);
		}
		if (promoPlanOffers) commit({ scope: { campaignId: null }, displayOnPromoPlan: model.displayOnPromoPlan, promoPlanOffers });
	}

	function notify(key, params = [], color = 'warning') {
		if (!key) return;
		notice.text = params.length ? t(key, params) : t(key);
		notice.color = color;
		notice.visible = true;
	}

	function confirm({ title, text, icon = 'help', confirmLabel = 'Confirm', cancelLabel = 'Cancel' }) {
		return new Promise((resolve) => {
			Object.assign(confirmState, { visible: true, title, text, icon, confirmLabel, cancelLabel, resolve });
		});
	}

	const inform = ({ title, text, icon = 'info' }) => confirm({ title, text, icon, confirmLabel: 'Close', cancelLabel: '' });

	/** Opens the offer or product picker of the step ; resolves with the picked ids, or an empty list when closed. */
	function pick({ kind, title, items }) {
		return new Promise((resolve) => {
			Object.assign(pickerState, { visible: true, kind, title, items, resolve });
		});
	}

	function closePicker(ids = []) {
		const resolve = pickerState.resolve;
		Object.assign(pickerState, { visible: false, resolve: null });
		resolve?.(ids);
	}

	function closeConfirm(answer) {
		const resolve = confirmState.resolve;
		Object.assign(confirmState, { visible: false, resolve: null });
		resolve?.(answer);
	}

	/** Stores a patch through the legacy ; the promo plan selection follows the negotiation (`checkForOfferSelection`). */
	function commit(patch) {
		const write = target?._promoStepCommit;
		if (typeof write !== 'function' || !snapshot.value) return false;
		const campaignId = patch.scope?.campaignId;
		if (campaignId && !patch.displayOnPromoPlan) {
			const plan = autoSelectPromoPlan(snapshot.value, campaignId, modelWithPatch(snapshot.value.model, patch));
			if (plan) Object.assign(patch, plan);
		}
		if (campaignId && patch.scope.campaignOfferId && (patch.negotiated || patch.stated)) {
			const record = forecastRecordPatch(snapshot.value, getCampaign(snapshot.value, campaignId), patch.scope);
			if (record) patch.offerInfo = { ...(patch.offerInfo || {}), ...record };
		}
		try {
			const result = write(JSON.parse(JSON.stringify(patch)));
			if (result?.ok && result.snapshot) receive(result.snapshot);
			return !!result?.ok;
		} catch (error) {
			log('error', 'usePromoStep', `Commit failed: ${error.message}`);
			notify('PromoSaveError', [], 'error');
			return false;
		}
	}

	function applyResult(result) {
		if (!result?.ok) {
			notify(result?.error, result?.params);
			return false;
		}
		return commit({ scope: result.ref, negotiated: result.negotiated, stated: result.stated });
	}

	function action(payload) {
		return new Promise((resolve) => {
			const run = target?._promoStepAction;
			if (typeof run !== 'function') return resolve(null);
			try {
				run(JSON.parse(JSON.stringify(payload)), (result) => resolve(result || {}));
			} catch (error) {
				log('error', 'usePromoStep', `Action ${payload.type} failed: ${error.message}`);
				resolve(null);
			}
		});
	}

	const campaignOf = (campaignId) => getCampaign(snapshot.value, campaignId);
	const offerInfoOf = (offerKey) => snapshot.value?.model?.offerInfos?.find((oi) => oi._key === offerKey) || null;

	const ops = {
		nego: (change) => applyResult(applyNegoChange(snapshot.value, change)),
		statement: (change) => applyResult(applyStatementChange(snapshot.value, change)),
		free: (change) => applyResult(applyFreeChange(snapshot.value, change)),
		addFree: (ref, productId) => applyResult(addFree(snapshot.value, ref, productId)),
		removeFree: (ref, productId) => applyResult(removeFree(snapshot.value, ref, productId)),
		negotiateAll: (ref) => applyResult(negotiateAll(snapshot.value, ref)),
		async clearAllNegos(ref) {
			const ok = await confirm({ title: t('PromoDeleteAllNegosTitle'), text: t('PromoDeleteAllNegosText'), icon: 'delete_sweep' });
			if (ok) applyResult(clearAllNegos(snapshot.value, ref));
		},
		async setAllPresence(ref, presence) {
			if (presence !== PRESENCE.PRESENT && hasStatedData(snapshot.value, ref)) {
				const ok = await confirm({ title: t(presence === PRESENCE.ABSENT ? 'PromoAllAbsent' : 'PromoResetPresence'), text: t('PromoClearStatementText'), icon: 'warning' });
				if (!ok) return false;
			}
			return applyResult(setAllPresence(snapshot.value, ref, presence));
		},
		addAdditional(ref, productId, kind, input) {
			const result = addAdditional(snapshot.value, ref, productId, kind, input);
			if (!result.ok) {
				notify(result.error);
				return false;
			}
			return commit({ scope: result.ref, [kind === 'nego' ? 'negotiated' : 'stated']: result.rows });
		},
		removeAdditional(ref, productId, kind, line) {
			const result = removeAdditional(snapshot.value, ref, productId, kind, line);
			if (!result.ok) return notify(result.error);
			commit({ scope: result.ref, [kind === 'nego' ? 'negotiated' : 'stated']: result.rows });
		},
		setPromoPlan(ref, checked) {
			const plan = setOfferOnPromoPlan(snapshot.value, ref, checked);
			return plan ? commit({ scope: { campaignId: null }, ...plan }) : false;
		},
		setGlobalComment: (value) => commit({ globalComment: String(value || '') }),
		setOfferComment(ref, value) {
			const result = ref.offerKey ? applyLocalOfferComment(value) : applyOfferComment(value);
			return commit({ scope: ref, offerInfo: result.offerInfo });
		},
		setStatementEndDate(ref, value) {
			const result = applyStatementEndDate(snapshot.value, campaignOf(ref.campaignId), ref, value);
			if (!result.ok) {
				if (result.error)
					notify(
						result.error,
						(result.params || []).map((d) => (d ? new Date(d).toLocaleDateString(snapshot.value.locale) : ''))
					);
				return false;
			}
			return commit({ scope: ref, offerInfo: result.offerInfo });
		},
		async setLocalOfferDate(ref, field, value, hasPointsHistory) {
			const campaign = campaignOf(ref.campaignId);
			const offerInfo = offerInfoOf(ref.offerKey);
			if (!offerInfo) return false;
			if (hasPointsHistory) {
				const ok = await confirm({ title: t('Are you sure?'), text: t('OfferDateChangeHasPointsRequestWarning'), icon: 'warning', confirmLabel: 'Valid' });
				if (!ok) return false;
			}
			const result = applyLocalOfferDate(snapshot.value, campaign, offerInfo, field, value);
			if (!result.ok) {
				notify(result.error, result.error === 'OfferStatementEndDateOutOfRange' ? [new Date(result.params[0]).toLocaleDateString(snapshot.value.locale), result.params[1] ? new Date(result.params[1]).toLocaleDateString(snapshot.value.locale) : ''] : []);
				return false;
			}
			return commit({ scope: ref, offerInfo: result.offerInfo });
		},
		async addLocalOffer(campaignId) {
			const campaign = campaignOf(campaignId);
			const offerIds = await pick({ kind: 'offer', title: t('AddOfferPromoLocale'), items: localOfferCandidates(campaign).map((offer) => ({ id: offer.id, name: offer.label, description: offer.mechanismLabel })) });
			if (!offerIds.length) return;
			const offers = createLocalOffers(snapshot.value, campaign, offerIds, () => crypto.randomUUID());
			commit({ scope: { campaignId }, newOfferInfos: offers.map((o) => ({ ...o, startDate: o.startDate.toISOString() })) });
		},
		async removeLocalOffer(ref) {
			const ok = await confirm({ title: t('Are you sure?'), text: t('PromoDeleteLocalOfferText'), icon: 'delete' });
			if (ok) commit({ scope: ref, removedOfferKey: ref.offerKey });
		},
		async addLocalProducts(ref) {
			const offerInfo = offerInfoOf(ref.offerKey);
			if (!offerInfo) return;
			const campaign = campaignOf(ref.campaignId);
			const productIds = await pick({ kind: 'product', title: t('AddProductOfferPromoLocale'), items: localProductCandidates(snapshot.value, campaign, offerInfo) });
			if (productIds.length) applyResult(addLocalProducts(snapshot.value, campaign, offerInfoOf(ref.offerKey) || offerInfo, productIds));
		},
		async removeLocalProduct(ref, productId) {
			const ok = await confirm({ title: t('Are you sure?'), text: t('PromoDeleteLocalProductText'), icon: 'delete' });
			const offerInfo = offerInfoOf(ref.offerKey);
			if (ok && offerInfo) applyResult(removeLocalProduct(snapshot.value, offerInfo, productId));
		},
		orderLine(ref, productId, isFree = false) {
			const check = checkLineOrder(snapshot.value, validation.value, ref, productId, isFree);
			if (!check.ok) return notify(check.error);
			action({ type: 'orderProduct', ...ref, productId, isFree });
		},
		orderOffer: (ref) => {
			if (validation.value?.errors?.some((e) => e.campaignId === ref.campaignId && (ref.offerKey ? e.offerKey === ref.offerKey : e.campaignOfferId === ref.campaignOfferId && !e.offerKey))) return notify('CannotOrderWithErrors');
			action({ type: 'orderOffer', ...ref });
		},
		uploadPhoto: (payload) => action({ type: 'uploadPhoto', ...payload }),
		deletePhoto: (payload) => action({ type: 'deletePhoto', ...payload }),
		generatePromoPlan: () => action({ type: 'generatePromoPlan', excludedPlanKeys: forecastPlanKeys(snapshot.value) }),
		async deactivateForecast(ref) {
			const check = canDeactivateForecast(snapshot.value, campaignOf(ref.campaignId), ref);
			if (check.reason === 'PromoOfferEndDatePassed') return inform({ title: t('PromoOfferEndDatePassedTitle'), text: t('PromoOfferEndDatePassedText'), icon: 'event_busy' }).then(() => false);
			if (!check.ok) return false;
			const ok = await confirm({ title: t('PromoForecastDeactivateTitle'), text: t('PromoForecastDeactivateText'), icon: 'toggle_off' });
			return ok ? commit(deactivateForecastPatch(ref)) : false;
		},
		run: (payload) => action(payload),
	};

	const format = {
		number: (value, digits = 0) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString(snapshot.value?.locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }) : ''),
		money: (value) => `${format.number(value, 2)} ${snapshot.value?.currency || ''}`.trim(),
		uc: (value) => `${format.number(value)} ${t('UC')}`,
		date: (value) => (value && !isNaN(new Date(value).getTime()) ? new Date(value).toLocaleDateString(snapshot.value?.locale) : ''),
		input: (value) => toInputDate(value),
	};

	const api = { snapshot, validation, notice, confirmState, closeConfirm, pickerState, closePicker, compact, attach, receive, notify, ops, format, t };
	provide(KEY, api);
	return api;
}

export function usePromoStep() {
	return inject(KEY);
}
